import { get } from 'svelte/store';
import { environment } from '../environment/environment.js';

import { writable as storedWritable } from '../stores/_localStore.js';

// Factory function to create a team member with default values,
// including a default quantity of 1 per product.
function createTeamMember(overrides = {}) {
  return {
    name: null,
    number: null,
    topSizeId: null,
    topQty: 1, // default set to 1 per row
    bottomSizeId: null,
    bottomQty: 1,
    hatSizeId: null,
    hatQty: 1,
    socksSizeId: null,
    socksQty: 1,
    accessorySizeId: null,
    accessoryQty: 1,
    ...overrides,
  };
}

const productTypes = environment.PRODUCT_TYPES;

const _teamDetails = storedWritable('teamDetails', [createTeamMember()]);
const _teamDetailsCopy = storedWritable('teamDetailsCopy', []);

// Helper for immutably updating a specific team member
function updateTeamMember(index, updatedData) {
  _teamDetails.update((team) => team.map((member, i) => (i === index ? { ...member, ...updatedData } : member)));
}

function addDefaultTeamMember(name, number) {
  const storeValue = get(_teamDetails);
  if (storeValue.length === 0) {
    _teamDetails.update((team) => [createTeamMember({ name, number })]);
    return;
  }
  if (storeValue[0].name === name && storeValue[0].number === number) {
    return;
  }
  updateTeamMember(0, { name, number });
}

function copyTeamDetails() {
  // Deep clone to avoid mutation issues between stores
  const storeValue = JSON.parse(JSON.stringify(get(_teamDetails)));
  _teamDetailsCopy.set(storeValue);
}

function restoreTeamDetails() {
  const storeValue = get(_teamDetailsCopy);
  _teamDetails.set(storeValue);
  _teamDetailsCopy.set([]);
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('teamDetailsCopy');
  }
}

function loadTeamDetails(teamDetails) {
  _teamDetails.set(teamDetails);
}

// With a consistent quantity of 1 per row, validation is simplified:
function validateTeamDetails(arrayOfSelectedProducts, numberRequired, nameRequired) {
  const storeValue = get(_teamDetails);
  for (const member of storeValue) {
    if (nameRequired && (!member.name || member.name === '')) return false;
    if (numberRequired && (!member.number || member.number === '')) return false;
    for (const product of arrayOfSelectedProducts) {
      if (member[`${product}SizeId`] === null) return false;
      // Ensure each quantity is at least 1.
      if (typeof member[`${product}Qty`] !== 'number' || member[`${product}Qty`] < 1) return false;
    }
  }
  return true;
}

function getInvalidRows(arrayOfSelectedProducts, numberRequired, nameRequired) {
  const storeValue = get(_teamDetails);
  const invalidRows = [];
  storeValue.forEach((member, index) => {
    if (nameRequired && (!member.name || member.name === '')) invalidRows.push(index);
    if (numberRequired && (!member.number || member.number === '')) invalidRows.push(index);
    for (const product of arrayOfSelectedProducts) {
      if (member[`${product}SizeId`] === null) invalidRows.push(index);
      if (typeof member[`${product}Qty`] !== 'number' || member[`${product}Qty`] < 1) {
        // New format sets quantity to 1 for each product type
        for (const product of productTypes) {
          setSizeQty(index, product, member[`${product}SizeId`], 1);
        }
      }
    }
  });
  return invalidRows;
}

function addRow() {
  _teamDetails.update((team) => [...team, createTeamMember()]);
}

function deleteRow(index) {
  _teamDetails.update((team) => team.filter((_, i) => i !== index));
}

function deleteRowsAfterIndex(index) {
  _teamDetails.update((team) => team.slice(0, index + 1));
}

function setSizeQty(index, productType, sizeId, qty) {
  updateTeamMember(index, {
    [`${productType}SizeId`]: sizeId,
    [`${productType}Qty`]: qty,
  });
}

// New utility function: calculates the total quantity for a given product type (e.g. 'top')
// Since each row starts with qty = 1, this sums up all the row quantities.
function getTotalQty(productType) {
  const storeValue = get(_teamDetails);
  return storeValue.reduce((total, member) => {
    const qty = Number(member[`${productType}Qty`]) || 0;
    return total + qty;
  }, 0);
}

export const teamDetails = {
  subscribe: _teamDetails.subscribe,
  set: _teamDetails.set,
  update: _teamDetails.update,
  addDefaultTeamMember,
  copyTeamDetails,
  loadTeamDetails,
  restoreTeamDetails,
  validateTeamDetails,
  getInvalidRows,
  addRow,
  deleteRow,
  deleteRowsAfterIndex,
  setSizeQty,
  getTotalQty,
};
